// src/pages/agent/Messages.js
// SMS inbox across all leads. Pulls from each lead's smsThread.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot, getDocs, where, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAgentAuth } from '../../lib/auth';
import { PageHeader, Card, BORDER, MID, LIGHT, BURG, BURG_DK, EmptyState } from '../../components/agent/UI';

// Is this conversation unread? True only when the most recent INBOUND
// message is newer than the last time someone opened the lead. Outbound
// texts never mark a conversation unread.
function unreadInfo(lead) {
  const lastIn = (lead.smsThread || []).filter(m => m.direction === 'in').slice(-1)[0];
  const lastInMs = lastIn?.sentAt?.toMillis?.() || 0;
  const readMs = lead.lastInboundReadAt?.toMillis?.() || 0;
  return { isUnread: lastInMs > 0 && lastInMs > readMs, lastInMs };
}

export default function Messages() {
  const { profile } = useAgentAuth();
  const [leads, setLeads] = useState([]);
  const navigate = useNavigate();

  // Resolve the shared pipeline owner: admins use their own; contract agents
  // use the inviting admin's (all leads live there). Without this a contract
  // agent would query their own empty subcollection and see no conversations.
  useEffect(() => {
    if (!profile?.uid) return;
    let cancelled = false;
    let unsub = () => {};
    (async () => {
      let pipelineUid = profile.uid;
      if (profile.role !== 'admin') {
        pipelineUid = profile.invitedByUid || null;
        if (!pipelineUid) {
          try {
            const aSnap = await getDocs(query(collection(db, 'agents'),
              where('role', '==', 'admin'), where('status', '==', 'active'), limit(1)));
            if (!aSnap.empty) pipelineUid = aSnap.docs[0].id;
          } catch {}
        }
        if (!pipelineUid) pipelineUid = profile.uid;
      }
      if (cancelled) return;
      const q = query(collection(db, 'agents', pipelineUid, 'leads'), orderBy('updatedAt', 'desc'));
      unsub = onSnapshot(q, snap => setLeads(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    })();
    return () => { cancelled = true; unsub(); };
  }, [profile?.uid, profile?.role, profile?.invitedByUid]);

  // Conversations with at least one message, unread ones pinned to the top.
  const withMessages = leads
    .filter(l => (l.smsThread || []).length > 0)
    .map(l => ({ ...l, _unread: unreadInfo(l) }))
    .sort((a, b) => {
      if (a._unread.isUnread !== b._unread.isUnread) return a._unread.isUnread ? -1 : 1;
      const at = a.updatedAt?.toMillis?.() || 0;
      const bt = b.updatedAt?.toMillis?.() || 0;
      return bt - at;
    });
  const unreadTotal = withMessages.filter(l => l._unread.isUnread).length;

  return (
    <>
      <PageHeader
        eyebrow="SMS"
        title="Messages"
        sub={unreadTotal > 0
          ? `${unreadTotal} conversation${unreadTotal > 1 ? 's' : ''} with a new reply.`
          : 'All conversations with property owners across your leads.'}
      />

      <Card padded={false}>
        {withMessages.length === 0 ? (
          <EmptyState
            title="No conversations yet"
            sub="Send an SMS from a lead's detail page and the thread will appear here."
          />
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {withMessages.map(l => {
              const last = (l.smsThread || []).slice(-1)[0];
              const isUnread = l._unread.isUnread;
              return (
                <li key={l.id} style={{ ...s.row, ...(isUnread ? s.rowUnread : {}) }} onClick={() => navigate(`/agent/leads/${l.id}`)}>
                  {isUnread ? <span style={s.dot} aria-label="New reply" /> : <span style={s.dotSpacer} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ ...s.who, ...(isUnread ? s.whoUnread : {}) }}>
                      {(l.owners || []).map(o => o.name).filter(Boolean).join(', ') || (l.owners || [])[0]?.phone || '—'}
                      {isUnread && <span style={s.newTag}>New</span>}
                    </div>
                    <div style={s.addr}>{l.address?.line1 || '(no address)'}</div>
                    <div style={{ ...s.preview, ...(isUnread ? s.previewUnread : {}) }}>
                      {last?.direction === 'in' ? '↩ ' : ''}{last?.body || ''}
                    </div>
                  </div>
                  <div style={s.time}>{last?.sentAt?.toDate?.()?.toLocaleString?.() || ''}</div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Card title="How SMS works for the team">
        <div style={{ fontSize: 13, color: MID, lineHeight: 1.65 }}>
          A single dedicated business number handles outbound SMS, inbound seller replies, and inbound voice calls.
          When you click a Quick Send pill on a lead, the message goes out from that number.
          When the seller replies, it lands back on the same lead's SMS thread automatically.
          Missed calls and voicemails post into the lead's notes as they happen.
          <br/><br/>
          Need to send a text but can't? It's almost always one of two things: you don't have a phone for the lead,
          or carrier registration is still pending. Contact your admin if the system says SMS isn't configured.
        </div>
      </Card>
    </>
  );
}

const s = {
  row: { padding: '16px 20px', borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', transition: 'background 0.12s ease', fontFamily: "'Inter', sans-serif" },
  rowUnread: { background: 'rgba(107,30,46,0.03)' },
  dot: { width: 8, height: 8, borderRadius: '50%', background: BURG, flexShrink: 0 },
  dotSpacer: { width: 8, flexShrink: 0 },
  who: { fontSize: 14, fontWeight: 500, color: '#111827', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 },
  whoUnread: { fontWeight: 700 },
  newTag: { fontSize: 10, fontWeight: 600, color: BURG, background: 'rgba(107,30,46,0.08)', borderRadius: 6, padding: '2px 8px', letterSpacing: 0, textTransform: 'none' },
  addr: { fontSize: 12, color: LIGHT, marginBottom: 4 },
  preview: { fontSize: 13, color: MID, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 520, lineHeight: 1.4 },
  previewUnread: { color: '#111827', fontWeight: 500 },
  time: { fontSize: 12, color: LIGHT, whiteSpace: 'nowrap' }
};
